import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckIcon, ChevronDownIcon, PackageCheckIcon, FileTextIcon } from 'lucide-react';
import { TOMOS, BONUS_DETAILS, type ValueRowId } from '../data/content';
type PricingValueRowProps = {
  id: ValueRowId;
  label: string;
  value: string;
};
function TomosDetail() {
  return <ol className="flex flex-col gap-3" data-id="element-167" data-mp="9lkrenbgsbym">
      {TOMOS.map(tomo => <li key={tomo.number} className="rounded-xl border border-line bg-paper p-3.5" data-id="element-168" data-mp="ehrorrpsof4y">
          <div className="flex items-start gap-3" data-id="element-169" data-mp="jgl3tokqce78">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-night font-display text-[14px] font-black text-amber" data-id="element-170" data-mp="jjgxv1tgiiqe">
              {tomo.number}
            </span>
            <div className="min-w-0" data-id="element-171" data-mp="sfzqecxs04mg">
              <p className="text-[10px] font-extrabold uppercase tracking-[0.16em] text-flame" data-id="element-172" data-mp="2q8vo6igbp213">
                Tomo {Number(tomo.number)} · {tomo.subtitle}
              </p>
              <h4 className="mt-0.5 font-display text-[15px] font-black leading-tight text-night" data-id="element-173" data-mp="1jthwlq0q1w68">
                {tomo.title}
              </h4>
            </div>
          </div>
          <p className="mt-2.5 text-[12.5px] leading-relaxed text-muted" data-id="element-174" data-mp="1f16x2o9w8ce7">{tomo.description}</p>
          <p className="mt-2.5 inline-flex items-start gap-1.5 rounded-full bg-teal-50 px-2.5 py-1.5 text-[11.5px] font-bold leading-snug text-teal-700" data-id="element-175" data-mp="1a1udqdi8dngd">
            <PackageCheckIcon className="mt-px h-3.5 w-3.5 shrink-0 text-teal-600" aria-hidden="true" data-id="element-176" data-mp="22rkr2kc1glbf" />
            Te llevás: {tomo.deliverable}
          </p>
        </li>)}
    </ol>;
}
function BonusDetail({
  id
}: {
  id: 'bono-1' | 'bono-2' | 'bono-3';
}) {
  const detail = BONUS_DETAILS[id];
  return <div className="rounded-xl border border-line bg-paper p-3.5" data-id="element-177" data-mp="1nujzcgtukqcy">
      <p className="text-[12.5px] leading-relaxed text-muted" data-id="element-178" data-mp="hfsszxlygdbj">{detail.intro}</p>
      <ul className="mt-3 flex flex-col gap-2" data-id="element-179" data-mp="cg6g5pa1gz39">
        {detail.bullets.map(bullet => <li key={bullet} className="flex items-start gap-2 text-[12.5px] leading-snug text-night" data-id="element-180" data-mp="cij2eemtxvm4">
            <CheckIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-teal-600" aria-hidden="true" data-id="element-181" data-mp="34nqh6oondk7z" />
            {bullet}
          </li>)}
      </ul>
      <p className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-teal-50 px-2.5 py-1.5 text-[11.5px] font-bold text-teal-700" data-id="element-182" data-mp="2qn5z5uqk3sfj">
        <FileTextIcon className="h-3.5 w-3.5 text-teal-600" aria-hidden="true" data-id="element-183" data-mp="2vovdbj92sm7u" />
        {detail.format}
      </p>
    </div>;
}
export function PricingValueRow({
  id,
  label,
  value
}: PricingValueRowProps) {
  const [open, setOpen] = useState(false);
  const panelId = `detalle-${id}`;
  return <li className="py-1" data-id="element-184" data-mp="27vmw5nwj8edi">
      <button type="button" onClick={() => setOpen(current => !current)} aria-expanded={open} aria-controls={panelId} className="flex w-full items-start justify-between gap-3 py-2 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-night" data-id="element-185" data-mp="2cwavcwux01it">
        <span className="flex items-start gap-2 text-[13.5px] leading-snug text-night" data-id="element-186" data-mp="1jd39cdn43hy0">
          <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-teal-600" aria-hidden="true" data-id="element-187" data-mp="1fcazbi6f190o" />
          {label}
        </span>
        <span className="flex shrink-0 items-center gap-1.5" data-id="element-188" data-mp="1t8o6nyh8ju1f">
          <span className="text-[13.5px] font-bold text-muted" data-id="element-189" data-mp="1oammtbbzjqow">{value}</span>
          <ChevronDownIcon className={`h-4 w-4 text-muted transition-transform duration-300 ${open ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-190" data-mp="22axfmjwuvfcp" />
        </span>
      </button>

      <AnimatePresence initial={false} data-id="element-191" data-mp="2c1id240smdu1">
        {open ? <motion.div id={panelId} key="panel" initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3,
        ease: [0.4, 0, 0.2, 1]
      }} className="overflow-hidden" data-id="element-192" data-mp="2c338rwyug2th">
            <div className="mb-2 mt-1 rounded-2xl bg-teal-50/60 p-3" data-id="element-193" data-mp="eeg6l3u1qcx0">
              {id === 'protocolo' ? <TomosDetail data-id="element-194" data-mp="jc99no01yqt2" /> : <BonusDetail id={id} data-id="element-195" data-mp="jbpswam528gm" />}
            </div>
          </motion.div> : null}
      </AnimatePresence>
    </li>;
}